import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccount, useMsal } from '@azure/msal-react';
import { get, includes, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useReadLocalStorage } from 'usehooks-ts';

import { holdsGcRole, isUserAllowed } from 'app/app-utils';
import { LANG, LOCAL_STORAGE_KEYS, ROLE_PRIORITY } from 'app/constants';
import { useActiveRole } from 'app/use-active-role';
import { useGetMeQuery } from 'services/secure/user.api';

import { getAdminGroups } from './nav-items';

export type AccountVariant = 'none' | 'msal-allowed' | 'msal-not-allowed' | 'guest-checkpoint';
export type OverlayKind = 'admin' | 'account' | 'drawer' | null;

interface GuestCheckpointUser {
  name?: string;
}

interface Me {
  isOrgViewWhitelisted?: boolean;
}

/**
 * The header's single controller hook: resolves who's signed in (MSAL and/or a guest-checkpoint
 * localStorage user), what that means for nav/account-menu visibility (the spec's visibility
 * matrix), and owns the "only one panel/drawer open at a time" overlay state.
 *
 * Reads the active role via `app/use-active-role` rather than redux directly —
 * `app/active-role.slice.js` unconditionally imports `services/secureLubaApi`, which imports
 * `app/msal-utils.js`, whose top-level `await` jest 27 cannot load. Component tests mock this
 * hook's module instead of wiring a real store, which sidesteps that import chain entirely.
 */
export function useHeaderModel() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { accounts, instance } = useMsal();
  const currentAccount = instance.getActiveAccount() || get(accounts, '[0]');
  const accountInfo = useAccount(currentAccount);
  const idTokenClaims = get(accountInfo, 'idTokenClaims');
  const hasMsalAccount = !isEmpty(idTokenClaims);
  const isAllowed = isUserAllowed(idTokenClaims);

  const { userRoles, activeRole, isActiveAdmin, switchRole } = useActiveRole();

  // Skip until allowed: the header renders above LubaUserProvider, so this duplicates its own
  // fetch, which RTK Query dedupes into a single request.
  const { data: meData } = useGetMeQuery(undefined, { skip: !isAllowed });
  const me = meData as Me | undefined;
  const isOrgViewWhitelisted = me?.isOrgViewWhitelisted ?? false;

  const guestCheckpointUser = useReadLocalStorage<GuestCheckpointUser>(LOCAL_STORAGE_KEYS.CURRENT_CHECKPOINT_USER);
  const hasGuestCheckpoint = !isEmpty(guestCheckpointUser);

  const isPublicPath = Boolean(useMatch('/public/*'));
  const isGcPath = Boolean(useMatch('/gc/*'));
  const isAdminPath = Boolean(useMatch('/admin/*'));
  const isStatisticsPath = Boolean(useMatch('/admin/statistics/*'));
  const isOrgViewPath = Boolean(useMatch('/org-view/*'));
  // Statistika split off admin/* into its own top-level link, so the Administreerimine trigger's
  // own active state must exclude it explicitly, or both would show active at once.
  const isAdminOrOrgViewActive = (isAdminPath && !isStatisticsPath) || isOrgViewPath;

  // When both a guest-checkpoint user and an MSAL account exist, the guest-checkpoint identity
  // wins on /gc/* routes (where guest-checkpoint staff actually work) and the MSAL identity wins
  // everywhere else — matches `pages/hybrid-secure.js`, which authorizes /gc/* for either an
  // MSAL admin (held role, not the active one) or a guest-checkpoint user.
  let accountVariant: AccountVariant;
  if (hasGuestCheckpoint && hasMsalAccount) {
    accountVariant = isGcPath ? 'guest-checkpoint' : isAllowed ? 'msal-allowed' : 'msal-not-allowed';
  } else if (hasGuestCheckpoint) {
    accountVariant = 'guest-checkpoint';
  } else if (hasMsalAccount) {
    accountVariant = isAllowed ? 'msal-allowed' : 'msal-not-allowed';
  } else {
    accountVariant = 'none';
  }

  // Nav visibility follows the MSAL identity alone (isAllowed), independent of which account
  // menu variant is showing — on /gc/* with both a guest-checkpoint user and an MSAL admin
  // present, the account menu swaps to guest-checkpoint (above) but the nav must not disappear,
  // matching the old header's behaviour (it never considered guest-checkpoint state for nav at
  // all).
  const adminGroups = useMemo(
    () => (isAllowed ? getAdminGroups({ isAdmin: isActiveAdmin, isOrgViewWhitelisted }) : []),
    [isAllowed, isActiveAdmin, isOrgViewWhitelisted]
  );

  // A GuestPermit holder needs this nav to reach /gc even though they can never be the active
  // Admin; statistics stays admin-only (an active-role check, not a held one — see showAdminNav).
  const showGuestPermitsNav = isAllowed && holdsGcRole(idTokenClaims);
  const showStatisticsNav = isAllowed && isActiveAdmin;
  const showAdminNav = isAllowed && adminGroups.length > 0;
  const hasNavItems = showGuestPermitsNav || showStatisticsNav || showAdminNav;

  const switchableRoles = ROLE_PRIORITY.filter(role => includes(userRoles, role));
  const showRolePicker = accountVariant === 'msal-allowed' && switchableRoles.length >= 2;

  // The account menu already carries a language control for these two variants, so the bar
  // switch (public pages only) would be a redundant second control — see the spec's general rule.
  const accountMenuHasLanguage = accountVariant === 'msal-allowed' || accountVariant === 'guest-checkpoint';
  const showBarLanguageSwitch = isPublicPath && !accountMenuHasLanguage;

  const [openOverlay, setOpenOverlay] = useState<OverlayKind>(null);
  const closeOverlay = useCallback(() => setOpenOverlay(null), []);
  const toggleOverlay = useCallback((kind: Exclude<OverlayKind, null>) => {
    setOpenOverlay(current => (current === kind ? null : kind));
  }, []);

  // Only one panel/drawer open at a time is already guaranteed by `toggleOverlay` replacing
  // whatever was open; this effect additionally closes everything on navigation.
  useEffect(() => {
    setOpenOverlay(null);
  }, [location.pathname, location.search]);

  const changeLanguage = useCallback(
    (lng: string) =>
      i18n.changeLanguage(lng).then(() => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.LANG, lng);
      }),
    [i18n]
  );

  const msalLogout = useCallback(() => {
    instance.logout({ account: currentAccount });
  }, [instance, currentAccount]);

  const guestCheckpointLogout = useCallback(() => {
    navigate('/gc/login');
  }, [navigate]);

  const firstName: string =
    accountVariant === 'guest-checkpoint'
      ? guestCheckpointUser?.name ?? ''
      : String(get(idTokenClaims, 'given_name', '') ?? '');

  return {
    isAllowed,
    accountVariant,
    firstName,
    roleLabel: activeRole ? t(`roles.${activeRole}`) : '',
    switchableRoles,
    showRolePicker,
    activeRole,
    switchRole,
    adminGroups,
    showGuestPermitsNav,
    showStatisticsNav,
    showAdminNav,
    hasNavItems,
    isAdminOrOrgViewActive,
    showBarLanguageSwitch,
    changeLanguage,
    language: i18n.language as (typeof LANG)[keyof typeof LANG],
    msalLogout,
    guestCheckpointLogout,
    openOverlay,
    closeOverlay,
    toggleOverlay,
  };
}

export type HeaderModel = ReturnType<typeof useHeaderModel>;
